import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { usePage } from "../../hooks/usePage";
import { api, errMsg, newKey } from "../../lib/api";
import { formatEventDate, venueLine } from "../../lib/format";
import { PageHero } from "../../components/site/Blocks";
import { SNAPSHOT, SnapshotNotice } from "../../lib/snapshot";
import { InviteBanner, InviteError } from "../../components/registration/InviteBanner";
import StepIndicator from "../../components/registration/StepIndicator";
import VerificationTestCard from "../../components/payments/VerificationTestCard";
import StepCategory from "../../components/registration/StepCategory";
import StepDetails from "../../components/registration/StepDetails";
import StepPreferences from "../../components/registration/StepPreferences";
import StepPayment from "../../components/registration/StepPayment";
import StepConfirmation from "../../components/registration/StepConfirmation";

const FORM_KEY = "ciarb_reg_form";
const RESULT_KEY = "ciarb_reg_result";
const EMPTY = { category_id: "", comp_code: "", invite_code: "", title: "", first_name: "", last_name: "", email: "", phone_country_code: "+1", phone: "",
  organization: "", job_title: "", attendance_mode: "", dietary: "", accessibility: "", special_requests: "", consent: false, accept_policies: false, idempotency_key: "" };
const load = (k, d) => { try { return JSON.parse(sessionStorage.getItem(k)) || d; } catch { return d; } };

function validate(step, f, cat) {
  const e = {};
  if (step === 0 && !f.category_id) e.category_id = "Please select an attendee type.";
  if (step === 1) {
    ["title", "first_name", "last_name"].forEach((k) => { if (!f[k].trim()) e[k] = "This field is required."; });
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "Enter a valid email address.";
    if (!/^[0-9 ()-]{5,20}$/.test(f.phone)) e.phone = "Enter a valid phone number (digits, spaces, dashes).";
    (cat?.required_fields || []).forEach((k) => { if (!f[k]?.trim()) e[k] = "Required for this attendee type."; });
  }
  if (step === 2 && !f.attendance_mode) e.attendance_mode = "Please choose how you will attend.";
  return e;
}

const EventStrip = ({ event }) => (
  <ul className="mt-6 space-y-2 text-white/90">
    <li className="flex items-center gap-3"><CalendarDays size={20} aria-hidden="true" />{formatEventDate(event)}{!event.date_confirmed && " (to be confirmed)"}</li>
    <li className="flex items-center gap-3"><MapPin size={20} aria-hidden="true" />{venueLine(event)}</li>
    <li className="flex items-center gap-3"><Users size={20} aria-hidden="true" />{event.attendance_format} Attendance</li>
  </ul>
);

export default function Registration() {
  const { event, payment } = useSite();
  const { section } = usePage("registration");
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(() => load(FORM_KEY, EMPTY));
  const [result, setResult] = useState(() => load(RESULT_KEY, null));
  const [step, setStep] = useState(() => (load(RESULT_KEY, null) ? 3 : 0));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [params, setParams] = useSearchParams();
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const inviteParam = params.get("invite") || "";
  const verifyParam = params.get("verify") || "";
  const [verify, setVerify] = useState(null);
  const shownCategories = verify ? [{ ...verify.category, name: `TEST — ${verify.category.name}`, current_tier: null, prices: { in_person: 1, virtual: 1 }, currency: "BSD", complimentary_rule: "none" }] : invite ? [invite.category] : categories;
  const cat = shownCategories.find((c) => c.id === form.category_id);
  const intro = section("intro");

  useEffect(() => {
    const include = new URLSearchParams(window.location.search).get("category") || "";  // unlisted categories via their own link
    api.get("/public/registration-categories", { params: include ? { include } : {} }).then((r) => setCategories(r.data));
  }, []);
  useEffect(() => {
    if (!inviteParam || SNAPSHOT) return;
    api.post("/public/invitations/validate", { code: inviteParam }).then(({ data }) => {
      const [first, ...rest] = (data.invitee_name || "").split(" ");
      setInvite(data); setInviteError(""); setResult(null); setStep(0);
      setForm((f) => ({ ...EMPTY, phone_country_code: f.phone_country_code, category_id: data.category.id, invite_code: inviteParam,
        first_name: first || "", last_name: rest.join(" "), email: data.invitee_email || "",
        attendance_mode: data.category.attendance_modes?.length === 1 ? data.category.attendance_modes[0] : "" }));
    }).catch((e) => { setInvite(null); setInviteError(errMsg(e)); setForm((f) => ({ ...f, invite_code: "" })); });
  }, [inviteParam]);
  useEffect(() => {
    if (!verifyParam || SNAPSHOT) return;
    api.get(`/public/verification/${verifyParam}`).then(({ data }) => {
      if (data.kind !== "registration") return;
      setVerify({ ...data, token: verifyParam }); setResult(null); setStep(0);
      setForm((f) => ({ ...EMPTY, phone_country_code: f.phone_country_code, category_id: data.category.id, attendance_mode: data.attendance_mode }));
    }).catch((e) => toast.error(errMsg(e)));
  }, [verifyParam]);
  useEffect(() => { sessionStorage.setItem(FORM_KEY, JSON.stringify(form)); }, [form]);
  useEffect(() => { result ? sessionStorage.setItem(RESULT_KEY, JSON.stringify(result)) : sessionStorage.removeItem(RESULT_KEY); }, [result]);
  useEffect(() => { if (result && ["confirmed", "pending_approval"].includes(result.registration.registration_status)) setStep(4); }, [result]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setErrors({}); };
  const next = () => { const e = validate(step, form, cat); setErrors(e); if (!Object.keys(e).length) { setStep(step + 1); window.scrollTo(0, 0); } };
  const reset = () => { setForm(EMPTY); setResult(null); setStep(0); setInvite(null); if (inviteParam) setParams({}); };
  const clearInvite = () => { setInvite(null); setInviteError(""); setForm(EMPTY); setParams({}); };

  const submit = async () => {
    setSubmitting(true);
    const idem = form.idempotency_key || newKey();
    if (!form.idempotency_key) set({ idempotency_key: idem });
    try {
      const { data } = await api.post("/public/registrations", { ...form, idempotency_key: idem, verify_token: verify?.token || "" });
      setResult(data);
      if (!data.registration.quote.payment_required) setStep(4);
    } catch (e) { toast.error(errMsg(e)); } finally { setSubmitting(false); }
  };
  const onPaid = (registration) => { setResult((r) => ({ ...r, registration })); setStep(4); };

  return (
    <div data-testid="registration-page">
      <PageHero eyebrow="CIARB BAHAMAS BRANCH" title={intro?.heading || event.name} subtitle={intro?.body}><EventStrip event={event} /></PageHero>
      <div className="container-site max-w-5xl py-12">
        {!event.registration_open && <p className="mb-6 rounded bg-paleblue p-4 font-semibold text-navy" data-testid="registration-closed">Registration is currently closed.</p>}
        <SnapshotNotice className="mb-8" />
        {SNAPSHOT && inviteParam && <p role="status" className="mb-8 rounded border-2 border-dashed border-gold bg-[#FFFBF0] p-4 font-semibold text-navy" data-testid="invite-snapshot-notice">Review copy — not connected. Invitation links can only be used on the live registration site.</p>}
        {verify && step < 4 && <p role="status" className="mb-6 rounded border-2 border-burgundy bg-[#FCE8E9] p-4 font-semibold text-navy" data-testid="verification-banner">VERIFICATION checkout: {verify.label}. Your real card is charged BSD 1.00 through the live gateway. This link works once and expires at {verify.expires_at.slice(11, 16)} UTC.</p>}
        {invite && step < 4 && <InviteBanner invite={invite} onClear={clearInvite} />}
        {inviteError && !invite && <InviteError message={inviteError} />}
        <StepIndicator current={step} />
        <div className="mt-10">
          {step === 0 && <StepCategory categories={shownCategories} form={form} set={set} errors={errors} invite={invite} testCard={!verify && !invite && <VerificationTestCard kind="registration" options={categories} />} />}
          {step === 1 && <StepDetails form={form} set={set} errors={errors} category={cat} lockEmail={!!invite?.email_locked} />}
          {step === 2 && <StepPreferences form={form} set={set} errors={errors} category={cat} event={event} />}
          {step === 3 && <StepPayment form={form} set={set} category={cat} payment={payment} result={result} onSubmit={submit} submitting={submitting} onPaid={onPaid} onContinue={() => setStep(4)} invite={invite} verifyToken={verify?.token || ""} />}
          {step === 4 && result && <StepConfirmation result={result} onReset={reset} />}
        </div>
        {step < 4 && (
          <div className="mt-10 flex justify-between border-t border-slate-200 pt-6">
            <button type="button" className="btn-outline" onClick={() => setStep(step - 1)} disabled={step === 0 || !!result} data-testid="registration-back-btn">Back</button>
            {step < 3 && <button type="button" className="btn-primary" onClick={next} disabled={!event.registration_open} data-testid={`registration-step-${step + 1}-next-btn`}>Continue</button>}
            {step === 3 && result && <button type="button" className="btn-outline" onClick={reset} data-testid="registration-start-over-btn">Start over</button>}
          </div>
        )}
      </div>
    </div>
  );
}
